import {defineStore} from "pinia";
import {IndexedDbService} from "@/utilities/services/IndexedDbService";
import {generateBlocksFromFile, UploadSession} from "@/models/helpers/UploadSession";
import ProjectFile from "@/models/entities/ProjectFile";
import FileService from "@/utilities/services/entities/FileService";
import GuestService from "@/utilities/services/GuestService";
import {ErrorLocationType, ValidationStateType} from "@/utilities/enums/Enums";
import {Block} from "@/models/helpers/Block";
import {getHashForFile} from "@/utilities/helpers/FileHashingHelper";
import {currentRouteIsExternal} from "@/utilities/helpers/RoutingHelper";
import sleeper from "@/utilities/helpers/Sleeper";


export const useUploadSessionStore = defineStore('UploadSessionStore', {
    state: () => {
        return {
            validationState: ValidationStateType.none,
            totalPercentage: "0%",
            // block offset used to determine if some blocks were sent in a previous upload attempt maybe from another client
            blockOffset: 0,
            isPartialUpload: false,
            fileToUpload: null as File|null,
            uploadAbortController: null as AbortController|null
        }
    },
    actions: {
        async uploadCallback(uploadGuid:string, blockIndex:number, errorLocationType:ErrorLocationType, newPercentage:string) {
            if(newPercentage === "100%") {
                const session = await this.updateSessionInDb(uploadGuid, blockIndex, errorLocationType);
                if(!session) {
                    console.error("failed to update session")
                    return;
                }
                this.totalPercentage = `${~~(((session.sendBlockIndexes.length + this.blockOffset) / session.initalBlockCount)*100)}%`;
            }
        },
        async generateUploadSession(file:ProjectFile, ticketGuid:string|null=null, errorLocationType:ErrorLocationType):Promise<boolean> {
            const dbService = new IndexedDbService("uploadGuid");
            this.validationState = ValidationStateType.processing;
            // added small delay to give UI time to update because hashing a big file takes the whole UI thread -.-
            await sleeper(100)
            const fileHash = await getHashForFile(file.file as File);
            if(!fileHash) {
                this.validationState = ValidationStateType.invalid;
                return false;
            }
            this.validationState = ValidationStateType.valid;
            const session = new UploadSession(file, fileHash, ticketGuid);
            this.validationState = ValidationStateType.none;
            return await dbService.createOrUpdateItem(session, errorLocationType);
        },
        async getTotalUploadPercentage(uploadGuid:string, errorLocationType:ErrorLocationType):Promise<string|null> {
            const dbService = new IndexedDbService("uploadGuid");
            const session = await dbService.getItem<UploadSession>(uploadGuid, errorLocationType);
            if(!session) return "0%";

            const fileService = currentRouteIsExternal() ? new GuestService() : new FileService();

            const missingIndexes = await fileService.getMissingBlocks(uploadGuid, errorLocationType) ?? [];
            session.sendBlockIndexes = session.sendBlockIndexes.filter(s => !missingIndexes.includes(s))

            if(!await dbService.createOrUpdateItem(session, errorLocationType)) return null;

            this.blockOffset = session.initalBlockCount - missingIndexes.length - session.sendBlockIndexes.length;
            this.isPartialUpload = session.sendBlockIndexes.length > 0 || this.blockOffset > 0;

            // add the block offset in case the upload was started on another client/browser/whatever
            return `${~~(((session.sendBlockIndexes.length + this.blockOffset) / session.initalBlockCount)*100)}%`;
        },
        async uploadMissingBlocks(uploadGuid:string, isNewFile:boolean, errorLocationType:ErrorLocationType):Promise<boolean> {
            // UI response for that should be handled by UI using this (e.g. with vuelitation)
            if (!this.fileToUpload)  {
                console.error("file unknown to store")
                return false;
            }

            const dbService = new IndexedDbService("uploadGuid");
            const session = await dbService.getItem<UploadSession>(uploadGuid, errorLocationType);
            if(!session)
                throw new Error('Upload session not found');

            this.validationState = ValidationStateType.processing
            // added small delay to give UI time to update because hashing a big file takes the whole UI thread -.-
            await sleeper(100)
            const selectedFileHash = isNewFile ? null : await getHashForFile(this.fileToUpload);
            if (!isNewFile && selectedFileHash !== session.fileHash) {
                this.validationState = ValidationStateType.invalid
                return false;
            }
            this.validationState = ValidationStateType.valid

            const fileService = currentRouteIsExternal() ? new GuestService() : new FileService();

            let blockIndexesToSend = Array.from(Array(session.initalBlockCount).keys());
            if(!isNewFile){
                const missingBlocks = await fileService.getMissingBlocks(uploadGuid, errorLocationType);
                if(missingBlocks === null){
                    console.error("failed to get missing blocks for session")
                    return false;
                }

                blockIndexesToSend = missingBlocks;
            }

            const fileBlocks = generateBlocksFromFile(session.uploadGuid, session.initalBlockCount, this.fileToUpload);

            let noErrors = true;
            for (const blockIndex of blockIndexesToSend) {
                const blockToSend = fileBlocks.find((rb:Block) => rb.index === blockIndex);
                if (!blockToSend) {
                    console.log(`Block ${blockIndex} could not be send although requested by missing blocks`)
                    noErrors = false;
                    continue;
                }

                this.uploadAbortController = new AbortController();

                try {
                    const uploadResp = await fileService.uploadBlock(uploadGuid, blockToSend, this.uploadCallback, this.uploadAbortController.signal, errorLocationType);
                    if (!uploadResp) {
                        console.log(`Block ${blockIndex} could not be uploaded`)
                        noErrors = false;
                        continue;
                    }
                }
                catch (e:any) {
                    if (e.message === "canceled") {
                        noErrors = false;
                        break;
                    }

                    throw e;
                }

                // trigger recalculation of the totalPercentage, since the callback in the 'OnProgress, doesn't trigger 100%'
                await this.uploadCallback(uploadGuid, blockIndex, errorLocationType, "100%");
            }

            this.uploadAbortController = null;

            return noErrors;
        },
        async closeSession(uploadGuid:string, errorLocationType:ErrorLocationType):Promise<boolean> {
            const dbService = new IndexedDbService("uploadGuid");
            const fileService = currentRouteIsExternal() ? new GuestService() : new FileService();

            // tell backend upload is completed
            const completed = await fileService.completeUpload(uploadGuid, errorLocationType)
            if(!completed) return false;

            // remove session from browserDB
            return await dbService.deleteItem(uploadGuid, errorLocationType) ?? false; //TODO clean up stale uploads? -> on edit open check if isComplete is set
        },
        async updateSessionInDb(uploadGuid:string, blockIndex:number, errorLocationType:ErrorLocationType):Promise<UploadSession|null> {
            const dbService = new IndexedDbService("uploadGuid");

            const session = await dbService.getItem<UploadSession>(uploadGuid, errorLocationType);
            if(!session) return null;

            // add index if now already in list
            const sessionSet = (new Set(session.sendBlockIndexes)).add(blockIndex);
            session.sendBlockIndexes = [...sessionSet];

            const updateSuccessful = await dbService.createOrUpdateItem(session, errorLocationType);
            if(!updateSuccessful) return null;

            return session;
        }
    }
})
